/*! *********************************************************************
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import _concat from 'lodash/concat';
import _isArray from 'lodash/isArray';
import _findIndex from 'lodash/findIndex';
import EventEmitter from '../utils/EventEmitter';
import BrowserLanguageDetector from './BrowserLanguageDetector';
import StorageLanguageDetector from './StorageLanguageDetector';
import IMSLanguageDetector from './IMSLanguageDetector';
var i18nDefaultOptions = {
    availableLng: [],
    fallbackLng: 'en',
    changedLngEventName: 'i18nLngChanged',
};
/**
 * Detect the best language to use
 * Follow this order of detection
 * - browser cookie / session
 * - url parameter
 * - IMS language
 * - Browser cookie for Adobe.com
 * - Browser language
 * - default
 */
var I18nLanguageDetector = /** @class */ (function (_super) {
    __extends(I18nLanguageDetector, _super);
    function I18nLanguageDetector() {
        var _this = _super.call(this) || this;
        _this.options = i18nDefaultOptions;
        _this.detectors = [];
        _this.detect();
        return _this;
    }
    I18nLanguageDetector.prototype.init = function (options) {
        this.options = __assign(__assign({}, i18nDefaultOptions), options);
        this.cachedLang = undefined;
        this.detectors = [
            new StorageLanguageDetector(),
            new IMSLanguageDetector(),
            new BrowserLanguageDetector(),
        ];
        this.detect();
    };
    /**
     * Returns the language that should be used by the user.
     * It takes the preferred languages one by one and check if it matches exactly (like language+region)
     * then searches if only language matches.
     * If no result is found for the 1st preferred language it goes to the next preferred language and so on.
     *
     * @param {array} preferredLanguages List of preferred languages
     * @returns {string} the language can be `zz` or `zz-ZZ` based on preferred languages and available languages
     */
    I18nLanguageDetector.prototype.chooseLanguageFromPreferredLanguages = function (preferredLanguages) {
        var e_1, _a;
        if (!preferredLanguages)
            return this.fallbackLng;
        if (!_isArray(this.availableLng) || this.availableLng.length < 1) {
            return this.fallbackLng;
        }
        var languageDetected = null;
        var _loop_1 = function (pLang) {
            var idxFound = _findIndex(this_1.availableLng, function (aLang) {
                return aLang === pLang ||
                    aLang === pLang.split('-')[0] ||
                    aLang.split('-')[0] === pLang.split('-')[0];
            });
            if (idxFound !== -1) {
                languageDetected = this_1.availableLng[idxFound];
                return "break";
            }
        };
        var this_1 = this;
        try {
            for (var preferredLanguages_1 = __values(preferredLanguages), preferredLanguages_1_1 = preferredLanguages_1.next(); !preferredLanguages_1_1.done; preferredLanguages_1_1 = preferredLanguages_1.next()) {
                var pLang = preferredLanguages_1_1.value;
                var state_1 = _loop_1(pLang);
                if (state_1 === "break")
                    break;
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (preferredLanguages_1_1 && !preferredLanguages_1_1.done && (_a = preferredLanguages_1.return)) _a.call(preferredLanguages_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return languageDetected || this.fallbackLng;
    };
    /**
     * Detect language
     * @returns {array} An array of user's preferred languages
     */
    I18nLanguageDetector.prototype.detect = function () {
        var lngFound = [];
        this.detectors.forEach(function (detector) {
            var languageFounds = detector.detect();
            lngFound = _concat(lngFound, languageFounds);
        });
        lngFound = lngFound.filter(Boolean);
        if (lngFound.length < 1)
            return [this.fallbackLng];
        return lngFound;
    };
    Object.defineProperty(I18nLanguageDetector.prototype, "fallbackLng", {
        get: function () {
            return this.options.fallbackLng;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(I18nLanguageDetector.prototype, "availableLng", {
        get: function () {
            return this.options.availableLng;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(I18nLanguageDetector.prototype, "changedLngEventName", {
        get: function () {
            return this.options.changedLngEventName;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Change language and locale to lang and emit a `I18n.changedLngEventName` change event
     * @param {string} lang the language selected as bcp47 (https://tools.ietf.org/html/bcp47) ex: 'en' or 'en-US' for English (US)
     */
    I18nLanguageDetector.prototype.changeLanguage = function (lang) {
        if (this.availableLng.indexOf(lang) === -1)
            throw new Error("'" + lang + "' not available in i18n.availableLng. Available languages are : " + this.availableLng.join(','));
        // force cached language to the choosen one
        this.cachedLang = lang;
        var storage = new StorageLanguageDetector();
        storage.store(lang);
        this.emit(this.changedLngEventName, lang);
    };
    Object.defineProperty(I18nLanguageDetector.prototype, "language", {
        /**
         * Return the language that should be used for the user
         */
        get: function () {
            if (this.cachedLang)
                return this.cachedLang;
            this.cachedLang = this.chooseLanguageFromPreferredLanguages(this.detect());
            return this.cachedLang;
        },
        enumerable: false,
        configurable: true
    });
    return I18nLanguageDetector;
}(EventEmitter));
export default new I18nLanguageDetector();
